import {action, uiConstants, React, ReactDOM, jsondiff} from '@api'
import {ImageWrapperDefinition} from '@api/graphql'
import {IconName} from '@blueprintjs/core'
import {VipLocalStore} from '@modules'
import {CatalogItemSidebarDetailsStore} from '@modules'
import {observable, computed, autorun, runInAction} from 'mobx'
import {computedFn} from 'mobx-utils'
import {useContext} from 'react'
import {CatalogImageSourceConfig, IFlexLayoutInlineCatalogItemDetailsConfig, ItemVerticalAlign, StoresInstance} from '@stores'
import {IImageOperationInput} from '@stores/catalog/CatalogDialogsStore'
import {DraggableDescriptor} from 'react-beautiful-dnd'

export interface LocalImage {
  imgPath?: string,
  exId?: string
  id?: string,
  position?: number,
  label?: string,
  image?: { image, id }
}

export const CatalogItemCarouselStoreContext = React.createContext<CatalogItemCarouselStore>(undefined)
export const useCatalogItemCarouselStore = () => useContext(CatalogItemCarouselStoreContext)

export type ImageSourceName = string
export type ImageParentId = string

export class CatalogItemCarouselStore extends VipLocalStore {
  constructor(stores: StoresInstance, public details: CatalogItemSidebarDetailsStore) {
    super(stores)
    details.modal.carousel = this
  }

  registerEffects = () => {
    const toDispose = [
      autorun(() => {
        const {details: {localImages, isDirty}} = this

        if (!isDirty) {
          const images = localImages.slice()

          runInAction(() => {
            this.localImages = images
            this.originalLocalImages = images.slice()
            this.resetSelectedIndex()
          })
        }
      })
    ]

    return () => toDispose.forEach(d => d())
  }

  @computed get ddHandlers() {
    const {dragDrop: {ddHandlers}, details: {sidebar: {selectedPathItem}}} = this
    if (selectedPathItem) {
      return ddHandlers.get(selectedPathItem.idWithPath)
    }
    return null
  }

  /**
   * For an MPV, we have 3 sets of images to be shown in the carousel
   *  1. The MPV's images
   *  2. The style (if it's assigned) images
   *  3. Local images (uploaded from the user but not yet saved to C8
   **/

  @computed get selectedImageSourceFieldName() {
    const {selectedImage, details: {sidebar: {selectedLibraryDefinition}}} = this

    if (!selectedImage) return null
    return this.labelForImageSourceField(selectedImage.source)
  }

  workspaceForImageSource = computedFn((source: string) => {
    const {associatedWorkspace, associatedWorkspace: {catalog}} = this
    const f = catalog.findFieldInTree(source, true)
    return catalog.workspaceForField(f)
  })

  get associatedWorkspace() {
    const {details} = this
    const r = details.associatedWorkspace ?? this.catalog.workspace

    return r
  }

  imageSourceForImage = (image : ImageWrapperDefinition) : CatalogImageSourceConfig => {
    const {associatedWorkspace, details: {imageSources}} = this
    return imageSources.find(imageSource => imageSource.index === associatedWorkspace.catalog.index || imageSource.field === image.source)
  }

  @computed get workspaceImagesBySource() {
    return _.groupBy(this.localImages, 'source')
  }

  @computed get workspaceImagesBySource_original() {
    return _.groupBy(this.originalLocalImages, 'source')
  }

  @computed get workspaceImagesBySourceAndParent() {
    return Object.entries(this.workspaceImagesBySource).reduce((acc, [key, imagesBySource]) => {
      acc[key] = _.groupBy(imagesBySource, '__parent.id')
      return acc
    }, {})
  }

  @computed get workspaceImagesBySourceAndParent_original() {
    return Object.entries(this.workspaceImagesBySource_original).reduce((acc, [key, imagesBySource]) => {
      acc[key] = _.groupBy(imagesBySource, '__parent.id')
      return acc
    }, {})
  }

  getImagesDiffBySource = computedFn((source: string) => {
    const {workspaceImagesBySourceAndParent, workspaceImagesBySourceAndParent_original} = this

    return _.keys(workspaceImagesBySourceAndParent[source]).reduce((acc, parent) => {
      const o = workspaceImagesBySourceAndParent_original[source][parent].map(v => v.exId),
        m = workspaceImagesBySourceAndParent[source][parent].map(v => v.exId),
        diff = jsondiff.diff(o, m)

      return _.isEmpty(diff) ? acc: {...acc, [parent]: {diff, orig: o, modified: m}}
    }, {})
  })

  @computed get pendingReorderedImages() {
    const {workspaceImagesBySourceAndParent, getImagesDiffBySource} = this

    return _.keys(workspaceImagesBySourceAndParent).reduce((totalDiff, source) => {
      const diff = getImagesDiffBySource(source)

      return _.isEmpty(diff) ? totalDiff : {...totalDiff, [source]: diff}
    }, {})
  }

  @computed get associatedWorkspaceImages() {
    const { localImages, associatedWorkspaceImageSource } = this
    return localImages.filter(img => img.source === associatedWorkspaceImageSource?.field)
  }

  @computed get nonAssociatedWorkspaceImagesByGroup() {
    const { workspaceImagesBySource, associatedWorkspaceImageSource } = this
    return _.omit(workspaceImagesBySource, associatedWorkspaceImageSource?.field)
  }

  @computed get associatedWorkspaceImageSource() {
    const {associatedWorkspace, details: {imageSources}} = this
    return imageSources.find(imageSource => imageSource.id === associatedWorkspace.id)
  }

  labelForImageSourceField = computedFn((source: string) => {
    const {catalog} = this
    const f = catalog.findFieldInTree(source)
    if (f) return catalog.labelForField(f)

    return catalog.catalogItemName
  })

  @computed get selectedImage(): (ImageWrapperDefinition ) {
    const {localImages, selectedImageIndex} = this
    return _.isEmpty(localImages) ? undefined : localImages[selectedImageIndex]
  }

  @observable selectedImageIndex = 0
  @observable localImages: Array<ImageWrapperDefinition > = []
  @observable.ref originalLocalImages: Array<ImageWrapperDefinition> = []
  @observable _activeImageOperation: Partial<IImageOperationInput>

  set activeImageOperation(v) { this._activeImageOperation = v}

  @computed get activeImageOperation(): Partial<IImageOperationInput> {
    const {_activeImageOperation} = this
    if (_activeImageOperation) return _activeImageOperation

    const {pendingReorderedImages} = this

    return _.isEmpty(pendingReorderedImages) ? null : {
      imageAction: 'REORDER'
    }
  }

  @computed get hasImages() {
    return !_.isEmpty(this.localImages)
  }

  @computed get disableDeleteImage() {
    return /*this.isRelativeImage(this.selectedImageIndex) ||*/ this.selectedImageIndex < 0
  }

  isRelativeImage = computedFn((imageIndex: number): boolean => {
    const {details: {imageSources}, localImages, catalog} = this

    if (_.isEmpty(localImages)) return false

    // Add this condition if needed to get relevant image source || (src?.index == catalog?.index) 
    const imageSource = imageSources.find(src => (src.id === catalog.id))?.field ?? 'images',
      selectedImage = localImages[imageIndex]

    return !!(selectedImage && selectedImage?.source !== imageSource)
  })

  // TODO:need to rethink on this method as there is already method from SidebarDetailsStore called updateActiveImageId
  @action carousel_onChangeIndex = (index, source?: 'thumbnail' | 'carousel') => {
    this.selectedImageIndex = source === 'thumbnail' && this.selectedImageIndex === index && this.stores.purpose === 'inline-details' ? -1 : index
    if (this.selectedImageIndex !== -1) {
      this.details.selectedImageIndex = index
      const node = ReactDOM.findDOMNode(this.indexToRef[index]) as HTMLDivElement
      node?.scrollIntoViewIfNeeded()
    } else {
      this.details.selectedImageIndex = index
    }
  }

  @action carousel_onChangeCarouselExId = (exId?: string) => {
    this.selectedImageIndex = this.localImages?.findIndex(image => !!exId && image?.exId === exId)
  }

  @action navigateToPreviousCarouselItem = (cycle = true) => {
    const {selectedImageIndex, localImages} = this
    this.selectedImageIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : (cycle ? localImages.length - 1 : 0)
  }

  @action navigateToNextCarouselItem = (cycle = true) => {
    const {selectedImageIndex, localImages} = this
    this.selectedImageIndex = selectedImageIndex + 1 == localImages.length ? (cycle ? 0 : selectedImageIndex) : selectedImageIndex + 1
  }

  @action thumbnail_onDoubleClick = (index) => () => {
    const {carousel_onChangeIndex, details: {sidebar}} = this

    carousel_onChangeIndex(index, 'thumbnail')
    // sidebar.showImageFullscreen = true  // Unreliable
  }

  indexToRef: { [index: number]: React.ReactInstance } = {}
  @action setThumbnailRef = (index) => (ref) => {
    this.indexToRef[index] = ref
  }

  @observable showThumbnails = true
  @action toggleThumbnails = () => this.showThumbnails = !this.showThumbnails

  isDragging: boolean = false
  @observable dragImageThumbnail: LocalImage | any = null

  get dragImageThumbnailLocalImage() { return this.dragImageThumbnail as LocalImage}

  @action onDragStart = (image) => (e: React.DragEvent) => {
    e.stopPropagation()

    const {currentTarget} = e,
      dragIndex = currentTarget.getAttribute('data-index') && parseInt(currentTarget.getAttribute('data-index')),
      {dragDrop, catalog} = this

    // console.log(`onDragStart`, {image, dragIndex})

    dragDrop.DragDropItem_onDragStart(e, {dragType: 'images'}, catalog)

    e.dataTransfer.effectAllowed = 'all'

    this.isDragging = true
    this.dragImageThumbnail = image

    if (_.isNumber(dragIndex)) {
      this.dragIndex = dragIndex
      this.dragItemYPosition = e.clientY
    }
  }

  @action onThumbanilDragEnd = (e) => {
    if (e.source && e.destination) {
      this.reorderLocalImages(e.source, e.destination)
    }
  }

  getListStyle = (isDragging) => {
    return {}
  }

  get tab() { return this.stores.extra?.tab}
  getItemStyle = (draggableStyle, isDragging) => {
    const top = draggableStyle.top - 40,
      {tab} = this
    const r = {
      // styles we need to apply on draggables
      ...draggableStyle,

      // some basic styles to make the items look a bit nicer
      userSelect: 'none',
      padding: '2px',
      margin: `0 0 2px 0`,
      left: 5,
      top: (_.isNaN(top) ? 0 : top) - (tab ? tab.rect.y : 0)
    }

    return r
  }

  allowImageReordering = computedFn((image) => {
    const {associatedWorkspaceImageSource, associatedWorkspaceImages} = this

    return (associatedWorkspaceImageSource?.field === image?.source && associatedWorkspaceImages.length >= 1)
  })
  
  parseImageFromDroppable = ({droppableId, index} : DraggableDescriptor) => {
    const {workspaceImagesBySourceAndParent, localImages} = this,
      DROPPABLE_DELIMITER = '__',
      [_prefix, source, parent] = droppableId.split(DROPPABLE_DELIMITER),
      image = workspaceImagesBySourceAndParent[source][parent][index],
      imageIndex = localImages.findIndex(each => each === image)
    
    return {image, imageIndex}
  }
  
  @action reorderLocalImages = (source: DraggableDescriptor, dest: DraggableDescriptor) => {
    const { details: { updateFieldData }, localImages, parseImageFromDroppable } = this,
      s = parseImageFromDroppable(source), d = parseImageFromDroppable(dest)
    
    localImages.splice(s.imageIndex, 1)
    localImages.splice(d.imageIndex, 0, s.image)

    // Update the images in the catalogItemStore
    const images = localImages.filter(i => i.source === 'images')

    updateFieldData('images', images)
  }

  @action resetSelectedIndex = () => {
    const {stores, localImages} = this
    this.selectedImageIndex = stores.purpose === 'inline-details'
                         ? (localImages.length > 0 ? -1 : 0)
                         : 0
  }

  @action dragReset = () => {
    this.isDragging = false
    this.isDragOver = false
    this.dragImageThumbnail = null
    this.imageData = null
    this.dragOverIndex = null
  }

  @action removeCatalogItemLocalImage = (index) => {
    const {details: {updateFieldData}, localImages} = this,
      images = [...localImages]

    images.splice(index, 1)
    updateFieldData(`images`, images)
  }

  @action removeCatalogItemImage = (image) => {
    const {details: {manageImage}} = this
    manageImage('', 'delete', image)
  }

  @action removeCatalogItemImage_onClick = () => {
    this.removeCatalogItemImage(this.selectedImage)
  }

  @action removeCatalogItemImage_onDrop = () => {
    this.removeCatalogItemImage(this.dragImageThumbnailLocalImage)
    this.dragReset()
  }

  @observable isDragOver = false
  @action onDragOver = (e: React.DragEvent) => {
    this.isDragOver = true
  }

  @action onDragLeave = () => {
    this.isDragOver = false
  }

  @observable autoplay = false
  @action onToggleAutoplay = () => {
    this.autoplay = !this.autoplay
  }

  @action addImages = async (e) => {
    const files = e?.target?.files,
      {catalog: {img: {readFileImages}}} = this,
      fileImages = await readFileImages(files)
    this.onAddImages(fileImages, false)
  }

  private onAddImages = async (files, isSameSource: boolean) => {
    const {details: {isOffline, data, updateFieldData}, localImages, dragOverIndex, catalog} = this
    let images = _.cloneDeep(data?.images) || [],
      firstImage = !images.length && files.length

    _.forEach(Array.from(files).reverse(), file => {
      // images.splice(imageDragOverIndex, 0, file)
      // console.log(file)
      if (_.isNumber(dragOverIndex)) {
        images.splice(dragOverIndex, 0, file)
      } else {
        images.unshift(file)
      }
    })
    if (isOffline) {
      const uploadedImages = await catalog.img.uploadImagesToC8(images.filter(image => image?.file))
      images = [...images.filter(image => !image?.file), ...uploadedImages]
    }
    this._activeImageOperation = {
      entityExId: data?.exId,
      originalImageId: isSameSource && (localImages[dragOverIndex]?.exId ?? '') || '',
      index: catalog.index,
      imageAction: 'UPLOAD'
    }
    updateFieldData('images', images)
    if (!(isOffline && firstImage)) {
      data.images = images
    }
    await this.details.mutationSaveCatalogItemDetails()
    this._activeImageOperation = null
  }

  @action onImageDrop = async (e: React.DragEvent, allowReorder: boolean = false) => {
    const {dataTransfer: dt} = e,
      {details: {data, updateFieldData}, localImages, dragOverIndex, catalog, catalog: {index, img: {readImageFromDataTransfer}, site: {onError}}} = this

    if (this.dragImageThumbnail == null) {
      allowReorder = !localImages.find(image => image.source === catalog.index || '') === undefined && localImages?.filter(image => image.source === catalog.index || '')?.length > 1
      const files = await readImageFromDataTransfer(dt)
      this.onAddImages(files, allowReorder)
      // } else if (allowReorder && dragOverIndex !== null && data?.images?.length > 1 && dragImageThumbnail?.exId !== localImages[dragOverIndex]?.exId) {

      // this.activeImageOperation = {
      //   replacementImageId: localImages[dragOverIndex].exId,
      //   originalImageId: dragImageThumbnail.exId,
      //   entityExId: data?.exId,
      //   index: index,
      //   imageAction: 'REORDER'
      // }

      // debugger
      // // Local images reordering
      // const images = _.cloneDeep(data?.images) || [],
      //   toReplaceImageIndex = images.findIndex(item => item.exId === dragImageThumbnail.exId),
      //   {entityExId, replacementImageId, originalImageId} = this.activeImageOperation
      // images.splice(dragOverIndex, 0, images.splice(toReplaceImageIndex, 1)[0])

      // if (entityExId && replacementImageId && originalImageId && index) {
      //   updateFieldData('images', images)
      // } else {
      //   const errString: string = `All parameters are required to reorder an image originalImageId: ${originalImageId}, replacementImageId:${replacementImageId} Entity id: ${entityExId}, Entity Index: ${index}`
      //   // console.log(errString)
      //   onError(errString)
      // }
    } else if (allowReorder && dragOverIndex !== null && data?.images?.length > 1) {
      const images = _.cloneDeep(data?.images) || []
      images.splice(dragOverIndex, 0, images.splice(this.dragIndex, 1)[0])

      updateFieldData('images', images)
    }
    // data.images = images
    // showConfirmationProductPopup({ message: strings.imageDropMessage, show: true, operation: 'UPLOAD_IMAGE', id: data })
    // this.dragReset()
  }

  // @action onImageReorderDrop = () => {
  //   const {localImages, imageDragOverIndex, dragImageThumbnailLocalImage} = this,
  //     index = localImages.findIndex(item => item.exId === dragImageThumbnailLocalImage.exId)

  //     debugger
  //   this.localImages.splice(imageDragOverIndex, 0, localImages.splice(index, 1)[0])
  //   console.warn(`nyi - reorder images on backend ${imageDragOverIndex} -> ${index}`, this.localImages)
  //   //api call for reordering
  //   this.dragReset()
  // }

  @observable dragOverIndex: number | null = null
  @observable imageData: any = null
  @observable draggingDirection: 'down' | 'up'
  dragIndex: number
  dragItemYPosition: number

  @action setDragDirection(currentDragYPosition: number) {
    const {dragItemYPosition} = this

    this.draggingDirection = currentDragYPosition > dragItemYPosition ? 'down' : 'up'
  }

  @action onDragEnterImage = (e: React.DragEvent, index: number, data) => {
    e.preventDefault()
    e.stopPropagation()

    const {currentTarget} = e
    const dropIndex = currentTarget.getAttribute('data-index') && parseInt(currentTarget.getAttribute('data-index'))

    //console.log(`onDragEnterImage`, {dragIndex: this.dragIndex, dropIndex})

    if (this.dragIndex != dropIndex) {
      this.setDragDirection(e.clientY)
      this.dragOverIndex = dropIndex
    } else {
      this.dragOverIndex = null
    }
    // this.imageData = data
  }

  @action onDragOverImage = (e) => {
    e.preventDefault()
  }

  @action onDragLeaveImage = (e) => {
    e.preventDefault()
    e.stopPropagation()

    // Use pointer-events: none on children to prevent invalid drag leaves - this code is too expensive to be firing constantly
    if (_.isNumber(this.dragOverIndex)) {
      const {currentTarget} = e

      const dropIndex = currentTarget.getAttribute('data-index')
      if (!dropIndex) {
        this.dragOverIndex = null
        this.imageData = null
      }
    }
  }

  @observable annotation = {}

  @observable annotations = []

  @action onChangeAnnotation = (annotation) => {
    this.annotation = annotation
  }

  @action onSubmitAnnotation = (annotation) => {
    const {geometry, data} = annotation

    this.annotation = {}
    this.annotations = [
      ...this.annotations, {
        geometry,
        data: {
          ...data,
          id: Math.random()
        }
      }
    ]
  }

  @observable isNavigateAnimating = false
  @observable carouselAnimationInterval = 1000
  doneAnimatingTimeout: number
  onSwipe_throttled = (val: number) => {
    if (this.isNavigateAnimating) return

    this.isNavigateAnimating = true
    // console.log(`onSwipe_debounced`, val)
    if (val > 0) { this.navigateToNextCarouselItem(false)} else { this.navigateToPreviousCarouselItem(false)}

    if (this.doneAnimatingTimeout) {
      window.clearTimeout(this.doneAnimatingTimeout)
    }
    this.doneAnimatingTimeout = window.setTimeout(() => {
      this.isNavigateAnimating = false
    }, this.carouselAnimationInterval)
  }

  get config() {
    return this.tab?.config as IFlexLayoutInlineCatalogItemDetailsConfig
  }

  @computed get vAlign() {
    return this.config?.settings?.verticalAlign ?? 'top'
  }

  @computed get verticalAlignIcon() {
      return verticalAlignIcons[this.vAlign];
  }
  @action changeVerticalAlign = (align: ItemVerticalAlign) => {
    this.config.settings.verticalAlign = align
  }

  @computed get contentsAlignSelf() {
    return verticalAligns[this.vAlign];
  }
}

export const verticalAlignIcons : Record<ItemVerticalAlign, IconName> = {
  top: "alignment-top",
  center: "alignment-vertical-center",
  bottom: "alignment-bottom",
  stretch: "alignment-vertical-center",
}

export const verticalAligns : Record<ItemVerticalAlign, string> = {
  top: "flex-start",
  center: "center",
  bottom: "flex-end",
  stretch: "stretch",
}


// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/CatalogItemImageCarouselStore.tsx